import { of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { mergeMap, catchError, tap, filter } from "rxjs/operators";
import { ofType } from "redux-observable";
import { push } from "connected-react-router";
import qs from "qs";
import addNotification from "react-push-notification";
import { getBaseUrl, setToken } from "frame";
import { HOME_ROUTE } from "features/home/home-routes";
import { iconDataUrl } from "utils/files";
import fromTypes from "../actions/types";

import { onLoginFailed, onLoginFulfilled, onTokenReceived } from "../actions";
import { subscriptionWarningSet } from "features/account/actions";

// NOTE:
// We intentionally do NOT request notification permission here.
// If the user has already granted permission, we can show a native welcome notification;
// otherwise we do nothing (prevents unexpected permission prompts on login).
const sendWelcomePushNotification = () => {
	try {
		if (typeof window === "undefined" || typeof Notification === "undefined") {
			return;
		}

		if (Notification.permission !== "granted") {
			return;
		}

		addNotification({
			title: "Welcome back",
			message: "You're signed in.",
			theme: "darkblue",
			native: true,
			icon: iconDataUrl,
			duration: 5000,
		});
	} catch (e) {
		// Best-effort only: never block login on notification errors.
	}
};

const requestBody = (emailAddress, password) => {
	return {
		username: emailAddress,
		password,
		grant_type: "password",
	};
};

const saveAuthorizationToken = (data) => {
	setToken(data.access_token, data.expires_in);
};

const redirect = (router) => {
	if (
		router &&
		router.location &&
		router.location.search &&
		router.location.search.includes("?location=")
	) {
		if (
			router.location.search.length >
			router.location.search.indexOf("=") + 2
		) {
			let value = router.location.search.substring(
				router.location.search.indexOf("=") + 2
			);
			return value;
		}
	}
	return HOME_ROUTE;
};

const loginEffect = (action$, state$) =>
	action$.pipe(
		ofType(fromTypes.AUTHORISATION_LOGIN_REQUESTED),
		filter(() => state$.value.authorisation.login.isValid),
		mergeMap(() =>
			ajax
				.post(
					`${getBaseUrl()}/oauth2/token`,
					qs.stringify(
						requestBody(
							state$.value.authorisation.login.emailAddress,
							state$.value.authorisation.login.password
						)
					)
				)
				.pipe(
					tap((ajaxResponse) => {
						saveAuthorizationToken(ajaxResponse.response);
						sendWelcomePushNotification();
					}),
					mergeMap((ajaxResponse) => {
						const response = ajaxResponse.response;
						const actions = [
							onTokenReceived(response),
							onLoginFulfilled(),
							push(redirect(state$.value.router)),
						];

						// If the account is in a grace period, dispatch a warning action
						// so the UI can show a popup on the next render.
						if (response.subscription_status === "GracePeriod") {
							actions.push(
								subscriptionWarningSet({
									subscriptionStatus: response.subscription_status,
									gracePeriodEnd: response.grace_period_end,
									daysRemaining: response.days_remaining,
								})
							);
						}

						return of(...actions);
					}),
					catchError((error) => of(onLoginFailed(error)))
				)
		)
	);

export default loginEffect;
